import _ from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { LoadingButton } from '@mui/lab'
import { Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemAvatar, ListItemText } from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import TextInputController from '@/components/ui/inputs/TextInputController'

const schema = yup.object({
  email: yup.string().email().required(),
})

type InviteByEmailFormValues = yup.InferType<typeof schema>

type InviteByEmailDialogProps = Readonly<{
  isSending?: boolean
  open: boolean
  onClose: () => void
  onSend?: (emails: string[]) => void
}>

export default function InviteByEmailDialog(props: InviteByEmailDialogProps) {
  const { isSending = false, open, onClose, onSend } = props
  const { t } = useTranslation()

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<InviteByEmailFormValues>({ defaultValues: { email: '' }, resolver: yupResolver(schema) })

  const [invitationEmails, setInvitationEmails] = useState<string[]>([])

  const handleClose = () => {
    reset()
    setInvitationEmails([])
    onClose()
  }

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleClose} open={open} sx={{ height: 'auto' }}>
      <DialogTitle sx={{ m: 0, p: 2 }}>Modal title</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseRoundedIcon />
      </IconButton>
      <DialogContent dividers>
        <Box
          component="form"
          onSubmit={handleSubmit(({ email }) => {
            setInvitationEmails((prevEmails) => _.concat(prevEmails, email))
            reset()
          })}
          mb={errors.email ? 3 : 0}
        >
          <TextInputController
            control={control}
            fullWidth
            label={t('field.email.label')}
            InputProps={{
              endAdornment: (
                <Button color="primary" variant="contained" size="small" type="submit" sx={{ mr: '12px' }} data-testid="network-invite-add-email">
                  {t('common:button.add.label')}
                </Button>
              ),
              sx: {
                '& .MuiOutlinedInput-input': {
                  paddingTop: 2,
                  paddingBottom: 2,
                },
              },
            }}
            name="email"
          />
        </Box>
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {invitationEmails.map((email) => (
            <ListItem
              key={`${email}`}
              disablePadding
              secondaryAction={
                <IconButton
                  onClick={() => {
                    setInvitationEmails((prevEmails) => prevEmails.filter((prevEmail) => prevEmail !== email))
                  }}
                >
                  <CloseRoundedIcon />
                </IconButton>
              }
              sx={{ py: 1 }}
            >
              <ListItemAvatar>
                <Avatar />
              </ListItemAvatar>
              <ListItemText primary={email} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ pt: 2 }}>
        <Button onClick={handleClose} variant="neutral">
          {t('common:button.cancel.label')}
        </Button>
        <LoadingButton loading={isSending} onClick={() => onSend?.(invitationEmails)} variant="contained" data-testid="network-invite-send">
          {t('common:button.send.label')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
